import { FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { isGoogleLoginConfigured, NewPasswordRequiredError } from '../../auth/cognito';
import { requestEmailVerification } from '../../auth/memberApi';
import './auth.css';

const Login = () => {
  const {
    completeGoogleLogin,
    completeNewPasswordLogin,
    isAuthenticated,
    login,
    loginWithGoogle,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordSession, setNewPasswordSession] = useState('');
  const [error, setError] = useState('');
  const [verificationMessage, setVerificationMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const statusMessage = (location.state as { message?: string } | null)?.message;
  const isSettingNewPassword = Boolean(newPasswordSession);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const oauthError = query.get('error_description') || query.get('error');
    if (oauthError) {
      setError(oauthError);
      navigate('/login', { replace: true });
      return;
    }
    if (!query.has('code')) return;

    setIsSubmitting(true);
    completeGoogleLogin(query)
      .then((completed) => {
        if (completed) navigate('/welcome', { replace: true });
      })
      .catch((requestError) => {
        setError(requestError instanceof Error ? requestError.message : 'Google login failed.');
        navigate('/login', { replace: true });
      })
      .finally(() => setIsSubmitting(false));
  }, [completeGoogleLogin, location.search, navigate]);

  if (isAuthenticated) {
    return <Navigate to="/welcome" replace />;
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setVerificationMessage('');
    setIsSubmitting(true);

    try {
      if (isSettingNewPassword) {
        await completeNewPasswordLogin(email, password, newPassword, newPasswordSession);
      } else {
        const verification = await requestEmailVerification(email);
        if (verification.verificationRequired) {
          setVerificationMessage('Check your email for verification code');
          return;
        }
        await login(email, password);
      }
      const destination = (location.state as { from?: string } | null)?.from;
      navigate(destination || '/welcome', { replace: true });
    } catch (requestError) {
      if (requestError instanceof NewPasswordRequiredError) {
        setEmail(requestError.email);
        setNewPasswordSession(requestError.session);
        setError('');
      } else {
        if (isSettingNewPassword) {
          setNewPasswordSession('');
          setNewPassword('');
        }
        setError(requestError instanceof Error ? requestError.message : 'Unable to log in.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="auth-shell">
      <div className="auth-hero">
        <p>Member access</p>
        <h1>{isSettingNewPassword ? 'Set your password.' : 'Welcome back.'}</h1>
        <span>
          {isSettingNewPassword
            ? 'Create a permanent password to finish your account setup.'
            : 'Log in to manage your Hogcloud member profile.'}
        </span>
      </div>
      <div className="auth-card">
        <form className="auth-form" onSubmit={handleSubmit}>
          {verificationMessage && <p className="auth-message success" role="status">{verificationMessage}</p>}
          {statusMessage && <p className="auth-message success">{statusMessage}</p>}
          {error && <p className="auth-message error" role="alert">{error}</p>}
          {isGoogleLoginConfigured && !isSettingNewPassword && (
            <>
              <button
                className="auth-google"
                type="button"
                disabled={isSubmitting}
                onClick={() => loginWithGoogle().catch((loginError) => {
                  setError(loginError instanceof Error ? loginError.message : 'Google login failed.');
                })}
              >
                Continue with Google
              </button>
              <div className="auth-divider"><span>or use email</span></div>
            </>
          )}
          <div className="auth-field">
            <label htmlFor="login-email">Email</label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="auth-field">
            <label htmlFor="login-password">Password</label>
            <input
              id="login-password"
              type="password"
              autoComplete={isSettingNewPassword ? 'one-time-code' : 'current-password'}
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={isSettingNewPassword}
            />
          </div>
          {isSettingNewPassword && (
            <div className="auth-field">
              <label htmlFor="new-password">New password</label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
              <p className="auth-help">
                At least 12 characters with uppercase, lowercase, a number, and a symbol.
              </p>
            </div>
          )}
          <button className="auth-submit" type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? 'Please wait...'
              : isSettingNewPassword
                ? 'Set password'
                : 'Login'}
          </button>
          <p className="auth-switch">
            New to Hogcloud? <Link to="/enroll">Enroll</Link>
          </p>
          <p className="auth-switch">
            Need a new password? <Link to="/reset-password">Change Password</Link>
          </p>
        </form>
      </div>
    </section>
  );
};

export default Login;
